import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { verifyEmailToken } from '../services/emailVerificationService';
import { useAuth } from '../context/AuthContext';
import './VerifyEmailPage.css';

const VerifyEmailPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { checkUser } = useAuth();
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [message, setMessage] = useState('');
  const hasVerifiedRef = React.useRef(false);

  useEffect(() => {
    const token = searchParams.get('token');

    if (!token) {
      setStatus('error');
      setMessage('Invalid verification link. No token provided.');
      return;
    }

    // Prevent duplicate verification calls
    if (hasVerifiedRef.current) {
      return;
    }
    hasVerifiedRef.current = true;

    const verifyEmail = async () => {
      try {
        setStatus('verifying');
        setMessage('Verifying your email...');

        const result = await verifyEmailToken(token);

        setStatus('success');
        setMessage('Email verified successfully! You can now log in. Redirecting to login...');

        // Refresh auth state to create profile
        await checkUser();

        // Redirect to login page after 3 seconds
        setTimeout(() => {
          navigate('/login?verified=true');
        }, 3000);
      } catch (error) {
        console.error('Verification error:', error);
        setStatus('error');
        
        if (error.message.includes('already verified')) {
          setMessage('This email has already been verified. You can log in now.');
        } else if (error.message.includes('expired')) {
          setMessage('Verification link has expired. Please request a new verification email.');
        } else if (error.message.includes('Invalid')) {
          setMessage('Invalid verification link. Please check your email or request a new link.');
        } else {
          setMessage('Verification failed. Please try again or contact support.');
        }
      }
    };

    verifyEmail();
  }, [searchParams, navigate, checkUser]);

  return (
    <div className="verify-email-page">
      <div className="verify-email-container">
        <div className={`verify-email-card ${status}`}>
          {status === 'verifying' && (
            <>
              <div className="spinner"></div>
              <h1>Verifying Email</h1>
              <p>{message}</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="success-icon">✓</div>
              <h1>Email Verified!</h1>
              <p>{message}</p>
              <div className="redirect-info">
                <p>You will be redirected automatically...</p>
              </div>
              <div style={{ marginTop: '16px' }}>
                <button
                  className="btn-primary"
                  onClick={() => navigate('/login?verified=true')}
                >
                  Go to Login
                </button>
              </div>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="error-icon">✕</div>
              <h1>Verification Failed</h1>
              <p>{message}</p>
              <div className="action-buttons">
                <button 
                  className="btn-primary"
                  onClick={() => navigate('/login')}
                >
                  Go to Login
                </button>
                <button 
                  className="btn-secondary"
                  onClick={() => navigate('/register')}
                >
                  Register Again
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerifyEmailPage;
